// Color tokens must match Invozio.PdfGenerator/Theming/InvoiceTheme.cs exactly - these are
// the *default* values the backend falls back to for any key the user hasn't overridden.
export const INVOICE_THEME_DEFAULTS: Record<string, string> = {
  Violet: '#7C4DEE',
  VioletDeep: '#6231E2',
  HeroFrom: '#F3EEFE',
  HeroTo: '#E7DEFC',
  PayBarBg: '#F4F0FE',
  TotalRowBg: '#F5F1FE',
  Ink: '#14121F',
  BodyInk: '#3B3757',
  Muted: '#8B87A3',
  Line: '#EBE7F6',
  RowLine: '#F2F0FA',
  Zebra: '#FBFAFF',
  Success: '#16A34A',
  Surface: '#FFFFFF',
  // Watermark is an 8-digit ARGB value (translucent) and only ever shows on the
  // draft/preliminary document, not the normal/credit invoice this preview renders - the
  // plain HTML <input type="color"> has no alpha channel, so it's not exposed as a picker
  // here. The backend still supports and defaults it; this is a UI-only omission.
  Watermark: '#332E1D6B',
};

export interface InvoiceThemeToken {
  key: string;
  labelKey: string;
  /** Short, plain-language explanation of what part of the invoice this color controls. */
  descriptionKey: string;
}

export interface InvoiceThemeTokenGroup {
  titleKey: string;
  tokens: InvoiceThemeToken[];
}

// Grouped so the toolbar reads as "accent, then table, then text, then surfaces" instead
// of 14 unlabeled swatches in a row - each still keeps its own tooltip with a unique name
// and a one-line description of what it changes.
export const INVOICE_THEME_GROUPS: InvoiceThemeTokenGroup[] = [
  {
    titleKey: 'INVOICE_THEME_GROUP_ACCENT',
    tokens: [
      { key: 'Violet', labelKey: 'INVOICE_THEME_VIOLET', descriptionKey: 'INVOICE_THEME_VIOLET_DESC' },
      { key: 'VioletDeep', labelKey: 'INVOICE_THEME_VIOLET_DEEP', descriptionKey: 'INVOICE_THEME_VIOLET_DEEP_DESC' },
      { key: 'HeroFrom', labelKey: 'INVOICE_THEME_HERO_FROM', descriptionKey: 'INVOICE_THEME_HERO_FROM_DESC' },
      { key: 'HeroTo', labelKey: 'INVOICE_THEME_HERO_TO', descriptionKey: 'INVOICE_THEME_HERO_TO_DESC' },
    ],
  },
  {
    titleKey: 'INVOICE_THEME_GROUP_PAYBOX',
    tokens: [
      { key: 'PayBarBg', labelKey: 'INVOICE_THEME_PAY_BAR_BG', descriptionKey: 'INVOICE_THEME_PAY_BAR_BG_DESC' },
    ],
  },
  {
    titleKey: 'INVOICE_THEME_GROUP_TABLE',
    tokens: [
      { key: 'TotalRowBg', labelKey: 'INVOICE_THEME_TOTAL_ROW_BG', descriptionKey: 'INVOICE_THEME_TOTAL_ROW_BG_DESC' },
      { key: 'RowLine', labelKey: 'INVOICE_THEME_ROW_LINE', descriptionKey: 'INVOICE_THEME_ROW_LINE_DESC' },
      { key: 'Zebra', labelKey: 'INVOICE_THEME_ZEBRA', descriptionKey: 'INVOICE_THEME_ZEBRA_DESC' },
    ],
  },
  {
    titleKey: 'INVOICE_THEME_GROUP_TEXT',
    tokens: [
      { key: 'Ink', labelKey: 'INVOICE_THEME_INK', descriptionKey: 'INVOICE_THEME_INK_DESC' },
      { key: 'BodyInk', labelKey: 'INVOICE_THEME_BODY_INK', descriptionKey: 'INVOICE_THEME_BODY_INK_DESC' },
      { key: 'Muted', labelKey: 'INVOICE_THEME_MUTED', descriptionKey: 'INVOICE_THEME_MUTED_DESC' },
    ],
  },
  {
    titleKey: 'INVOICE_THEME_GROUP_SURFACES',
    tokens: [
      { key: 'Surface', labelKey: 'INVOICE_THEME_SURFACE', descriptionKey: 'INVOICE_THEME_SURFACE_DESC' },
      { key: 'Line', labelKey: 'INVOICE_THEME_LINE', descriptionKey: 'INVOICE_THEME_LINE_DESC' },
      { key: 'Success', labelKey: 'INVOICE_THEME_SUCCESS', descriptionKey: 'INVOICE_THEME_SUCCESS_DESC' },
    ],
  },
];

// Points (not pixels) - matches Invozio.PdfGenerator/Theming/Typography.cs
// Sections.HeaderArea (110x34 px converted at 0.75pt/px) and the same
// min/max bounds InvoiceThemeColors.Resolve clamps to server-side.
export const DEFAULT_LOGO_WIDTH_PT = 82.5;
export const DEFAULT_LOGO_HEIGHT_PT = 25.5;
export const MIN_LOGO_DIMENSION_PT = 10;
export const MAX_LOGO_DIMENSION_PT = 260;
